import React, { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  ["pending", "En attente"],
  ["verified", "Validés"],
  ["rejected", "Refusés"],
];

export default function AdminDocuments() {
  const [tab, setTab] = useState("pending");
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async (t = tab) => {
    try {
      const { data } = await api.get(`/admin/documents?status=${t}`);
      setDocs(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const verify = async (doc_id) => {
    setBusy(doc_id);
    try {
      await api.post(`/admin/documents/${doc_id}/verify`);
      toast.success("Document validé");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const reject = async (doc_id) => {
    const reason = window.prompt("Motif du refus (visible par le dépanneur) :") || "";
    setBusy(doc_id);
    try {
      await api.post(`/admin/documents/${doc_id}/reject`, { reason });
      toast.success("Document refusé");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-documents-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-orange-600" />
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Administration</div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-4">Documents transporteurs</h1>

          <div className="flex gap-1 mb-4">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  tab === key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {docs.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">
              Aucun document {tab === "pending" ? "en attente" : tab === "verified" ? "validé" : "refusé"}.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-3">Dépanneur</th>
                    <th className="px-4 py-3">Document</th>
                    <th className="px-4 py-3">Téléversé</th>
                    <th className="px-4 py-3">Expire</th>
                    <th className="px-4 py-3">Empreinte</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map(d => (
                    <tr key={d.doc_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{d.transporter?.name || d.transporter_id}</div>
                        <div className="text-xs text-slate-500">{d.transporter?.company || d.transporter?.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{d.label}</div>
                        <div className="text-xs text-slate-500">{d.file_name}</div>
                        {d.reject_reason && <div className="text-xs text-red-600">Motif : {d.reject_reason}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{new Date(d.uploaded_at).toLocaleDateString("fr-FR")}</td>
                      <td className="px-4 py-3 text-slate-600">{d.expires_at || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-[10px] text-slate-400" title={d.file_hash}>
                          {d.file_hash?.slice(0, 12)}…
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <a
                            href={`${API_BASE}/documents/${d.doc_id}/file`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                            title="Voir le fichier"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {tab !== "verified" && (
                            <Button size="sm" onClick={() => verify(d.doc_id)} disabled={busy === d.doc_id}
                                    className="bg-emerald-600 hover:bg-emerald-700 h-8">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Valider
                            </Button>
                          )}
                          {tab !== "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => reject(d.doc_id)} disabled={busy === d.doc_id}
                                    className="h-8 text-red-600 border-red-200 hover:bg-red-50">
                              <XCircle className="w-3.5 h-3.5 mr-1" />Refuser
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
